import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import addressApi from "api/landlord/address";
import { TenantContact } from "types/landlord/address";
import { SectionCard } from "shared/app/SectionCard";
import { Row } from "views/applications-id-report/components/Row";

interface CurrentTenantsSectionProps {
  propertyId: number;
  initialTenants?: TenantContact[];
  // When provided, uses a unit-scoped cache key and skips the network fetch.
  // Prevents collision with the property-level /tenant_contacts cache entry.
  unitId?: number;
}

export const CurrentTenantsSection = ({
  propertyId,
  initialTenants,
  unitId
}: CurrentTenantsSectionProps) => {
  const queryKey = useMemo(
    () =>
      unitId
        ? ["units", String(unitId), "tenant_contacts"]
        : ["properties", String(propertyId), "tenant_contacts"],
    [propertyId, unitId]
  );

  const { data: tenants = [] } = useQuery<TenantContact[]>({
    queryKey,
    queryFn: () => addressApi.getPropertyTenantContacts(propertyId),
    initialData: initialTenants,
    initialDataUpdatedAt: initialTenants ? Date.now() : undefined,
    enabled: !unitId,
    staleTime: Infinity
  });

  if (!tenants.length) return null;

  return (
    <SectionCard title="Current tenants">
      <div className="space-y-6">
        {tenants.map((tenant) => (
          <div key={tenant.external_id} className="space-y-2">
            <p className="border-b border-alpha-40 pb-2 text-sm font-bold text-content-1000">
              Tenant
            </p>
            <div className="space-y-4">
              <Row title="First name" value={tenant.first_name} />
              <Row title="Last name" value={tenant.last_name} />
              <Row title="Email" value={tenant.email} />
            </div>
          </div>
        ))}
      </div>
    </SectionCard>
  );
};
